import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Users, Compass, Rocket, Share2, Target, Shield, Clock, Sparkles, GraduationCap, Lightbulb, ExternalLink, ChevronDown } from 'lucide-react';

// Logo URLs
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_4cb3e094-2c6c-4bf9-b6ba-50cf8fe1eb94/artifacts/zh1bc2r6_opensapiens_logo_transparent_dark.png";

// Questionnaire URLs
const QUESTIONNAIRE_LEARNER = "https://forms.office.com/Pages/ResponsePage.aspx?id=DQSIkWdsW0yxEjajBLZtrQAAAAAAAAAAAAa__abNzxRUNkJTVlFLNlhRQlpGOU1TMlI4RDI2NDE2Uy4u";
const QUESTIONNAIRE_EXPERT = "https://forms.office.com/Pages/ResponsePage.aspx?id=DQSIkWdsW0yxEjajBLZtrQAAAAAAAAAAAAa__abNzxRUQTY5REpET05GWVBQVE0zRE5VV0JXUjRZQi4u";

const Home = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0d1f35]" data-testid="home-page">
      {/* Subtle grid/tech texture overlay */}
      <div className="fixed inset-0 opacity-[0.02] sm:opacity-[0.02] pointer-events-none" 
           style={{
             backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2300f5ff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
           }} 
      />

      {/* Mobile-enhanced ambient glow effects */}
      <div className="fixed top-0 left-0 w-full h-[50vh] bg-gradient-to-b from-cyan-500/15 to-transparent pointer-events-none sm:hidden"></div>
      <div className="fixed bottom-0 left-0 w-full h-[40vh] bg-gradient-to-t from-purple-500/15 via-pink-500/10 to-transparent pointer-events-none sm:hidden"></div>
      
      {/* Desktop ambient glow effects */}
      <div className="hidden sm:block fixed top-1/4 left-1/4 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[150px] pointer-events-none"></div>
      <div className="hidden sm:block fixed bottom-1/4 right-1/4 w-[400px] h-[400px] bg-purple-500/10 rounded-full blur-[120px] pointer-events-none"></div>
      <div className="hidden sm:block fixed top-1/2 right-1/3 w-[300px] h-[300px] bg-pink-500/8 rounded-full blur-[100px] pointer-events-none"></div>
      
      {/* Navigation */}
      <nav className="relative z-50 border-b border-white/5">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo - Icon + Text */}
            <div className="flex items-center space-x-2 flex-shrink-0">
              <img
                src={LOGO_DARK}
                alt=""
                className="h-8 w-8 sm:h-10 sm:w-10 object-contain"
              />
              <span className="text-lg sm:text-xl font-semibold bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                OpenSapiens
              </span>
            </div>
            
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* PRÉ-BÊTA Badge - Hidden on mobile */}
              <div className="hidden lg:flex items-center space-x-3">
                <span className="flex items-center px-3 py-1.5 rounded-full bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border border-cyan-400/30">
                  <span className="w-2 h-2 bg-cyan-400 rounded-full mr-2 animate-pulse"></span>
                  <span className="text-cyan-400 text-sm font-medium tracking-wide">PRÉ-BÊTA</span>
                </span>
                <span className="text-gray-400 text-sm">CH Suisse</span>
              </div>
              
              <Button 
                variant="ghost" 
                className="text-gray-300 hover:text-white hover:bg-white/5 text-sm sm:text-base px-2 sm:px-4"
                onClick={() => navigate('/login')}
              >
                Connexion
              </Button>
              <Button
                className="bg-gradient-to-r from-cyan-500 to-cyan-400 hover:from-cyan-400 hover:to-cyan-300 text-gray-900 font-semibold px-3 sm:px-6 text-sm sm:text-base"
                onClick={() => navigate('/signup')}
              >
                Inscription
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* 1. Hero Section */}
      <section className="relative min-h-[calc(100vh-64px)] sm:min-h-[calc(100vh-80px)] flex items-center justify-center overflow-hidden pb-16">
        {/* Ambient glow effects */}
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-cyan-500/15 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-purple-500/10 rounded-full blur-[100px] pointer-events-none"></div>
        <div className="absolute top-1/2 right-1/3 w-[300px] h-[300px] bg-pink-500/8 rounded-full blur-[80px] pointer-events-none"></div>
        
        <div className="relative container mx-auto px-6 py-12 sm:py-20 text-center">
          {/* Badge */}
          <div className="inline-flex items-center px-3 sm:px-4 py-2 rounded-full bg-gradient-to-r from-pink-500/20 to-orange-500/20 border border-pink-400/30 mb-6 sm:mb-10">
            <span className="w-2 h-2 bg-pink-400 rounded-full mr-2 animate-pulse"></span>
            <span className="text-pink-300 text-xs sm:text-sm font-medium tracking-wider uppercase">
              Phase expérimentale — Nous construisons avec vous
            </span>
          </div>
          
          {/* Main Headline */}
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold leading-tight mb-6 sm:mb-8">
            <span className="text-white">Le savoir se partage.</span>
            <br />
            <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              L'humain se retrouve.
            </span>
          </h1>
          
          {/* Subtitle */}
          <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto mb-12 leading-relaxed">
            Une plateforme où chacun peut transmettre son expertise et apprendre des autres. 
            <span className="text-gray-300"> Un seul profil. Des échanges authentiques. Une approche éthique.</span>
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col items-center gap-4">
            <Button
              size="lg"
              className="bg-gradient-to-r from-cyan-500 to-cyan-400 hover:from-cyan-400 hover:to-cyan-300 text-gray-900 font-semibold text-lg px-10 py-6 rounded-xl shadow-lg shadow-cyan-500/25 transition-all hover:shadow-cyan-500/40 hover:scale-105"
              onClick={() => navigate('/signup')}
            >
              Commencer gratuitement
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            
            {/* Secondary CTA - Construisez avec nous */}
            <button
              onClick={() => document.getElementById('construisez-avec-nous')?.scrollIntoView({ behavior: 'smooth' })}
              className="group flex items-center gap-2 text-gray-400 hover:text-white transition-colors mt-2"
            >
              <span className="text-sm">Partagez votre expertise pour nous aider à construire</span>
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          {/* Scroll Down Indicator */}
          <button
            onClick={() => document.getElementById('what-is-opensapiens')?.scrollIntoView({ behavior: 'smooth' })}
            className="absolute bottom-4 sm:bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 sm:gap-2 text-white/30 hover:text-white/60 transition-colors cursor-pointer group"
            aria-label="Découvrir plus"
          >
            <span className="text-[10px] sm:text-xs uppercase tracking-widest hidden sm:block">Découvrir</span>
            <div className="relative">
              <ChevronDown className="h-5 w-5 sm:h-6 sm:w-6 animate-bounce" />
              <ChevronDown className="h-5 w-5 sm:h-6 sm:w-6 absolute top-1.5 sm:top-2 left-0 animate-bounce opacity-40" style={{ animationDelay: '0.15s' }} />
            </div>
          </button>
        </div>
      </section>

      {/* 2. What is OpenSapiens */}
      <section id="what-is-opensapiens" className="relative py-24 border-t border-white/5">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16">
            <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              Qu'est-ce qu'un OpenSapiens ?
            </span>
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Card 1 - Expert/Partager */}
            <div className="group relative bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-cyan-400/30 transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl mb-6 shadow-lg shadow-cyan-500/20">
                  <Share2 className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">
                  Partager son expertise
                </h3>
                <p className="text-gray-400 leading-relaxed mb-4">
                  Un <span className="text-cyan-400 font-semibold">OpenSapiens</span> est quelqu'un qui veut 
                  partager son expertise, transmettre ses compétences et <span className="text-white">aider les autres à progresser</span>.
                </p>
                <div className="pt-4 border-t border-white/10">
                  <p className="text-sm text-gray-500 mb-2 uppercase tracking-wider">Pourquoi nous sommes différents</p>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Nous vous permettons de transmettre votre expertise tout en la <span className="text-cyan-400 font-medium">valorisant immédiatement</span>, 
                    sans dépendre d'algorithmes ou d'intermédiaires.
                  </p>
                </div>
              </div>
            </div>

            {/* Card 2 - Learner/Apprendre */}
            <div className="group relative bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-pink-400/30 transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-pink-500/5 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl mb-6 shadow-lg shadow-pink-500/20">
                  <Target className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">
                  Apprendre des expériences prouvées
                </h3>
                <p className="text-gray-400 leading-relaxed mb-4">
                  Un <span className="text-pink-400 font-semibold">OpenSapiens</span> est aussi quelqu'un 
                  qui est ouvert à apprendre des autres, à développer de nouvelles compétences grâce 
                  à des <span className="text-white">experts confirmés</span>.
                </p>
                <div className="pt-4 border-t border-white/10">
                  <p className="text-sm text-gray-500 mb-2 uppercase tracking-wider">Des échanges réels</p>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Des conversations authentiques qui vous permettent d'<span className="text-pink-400 font-medium">atteindre réellement</span> vos objectifs d'apprentissage.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Sur OpenSapiens, vous pouvez être les deux : 
              <span className="text-white font-semibold"> apprendre, partager, grandir ensemble</span>
            </p>
          </div>
        </div>
      </section>

      {/* 3. How it works */}
      <section className="relative py-24 border-t border-white/5">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-6 text-white">
            Comment ça marche ?
          </h2>
          <p className="text-center text-gray-400 mb-16 max-w-2xl mx-auto">
            Une approche simple et humaine, sans les barrières habituelles
          </p>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Step 1 */}
            <div className="relative bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-cyan-400/20 transition-all group">
              <div className="absolute -top-4 -left-4 w-10 h-10 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl flex items-center justify-center text-white font-bold shadow-lg shadow-cyan-500/30">
                1
              </div>
              <div className="flex items-center justify-center w-14 h-14 bg-cyan-500/10 rounded-xl mb-6 group-hover:bg-cyan-500/20 transition-colors">
                <Users className="h-7 w-7 text-cyan-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-3">Un profil unique</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-4">
                Présentez qui vous êtes vraiment : vos compétences, votre parcours, ce que vous pouvez apporter et ce que vous cherchez à apprendre.
              </p>
              <div className="flex items-center text-cyan-400 text-sm">
                <Clock className="h-4 w-4 mr-2" />
                <span>5 min pour créer</span>
              </div>
            </div>
            
            {/* Step 2 */}
            <div className="relative bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-purple-400/20 transition-all group">
              <div className="absolute -top-4 -left-4 w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold shadow-lg shadow-purple-500/30">
                2
              </div>
              <div className="flex items-center justify-center w-14 h-14 bg-purple-500/10 rounded-xl mb-6 group-hover:bg-purple-500/20 transition-colors">
                <Compass className="h-7 w-7 text-purple-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-3">Trouvez les bonnes personnes</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-4">
                Pas d'algorithme opaque. Suivez les experts qui vous inspirent, découvrez des profils pertinents, et engagez des conversations directes.
              </p>
              <div className="flex items-center text-purple-400 text-sm">
                <Shield className="h-4 w-4 mr-2" />
                <span>Sans intermédiaire</span>
              </div>
            </div>
            
            {/* Step 3 */}
            <div className="relative bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-pink-400/20 transition-all group">
              <div className="absolute -top-4 -left-4 w-10 h-10 bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl flex items-center justify-center text-white font-bold shadow-lg shadow-pink-500/30">
                3
              </div>
              <div className="flex items-center justify-center w-14 h-14 bg-pink-500/10 rounded-xl mb-6 group-hover:bg-pink-500/20 transition-colors">
                <Rocket className="h-7 w-7 text-pink-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-3">Échangez et progressez</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-4">
                Sessions 1:1, mentorat, partage de ressources : vous décidez comment vous voulez interagir et valoriser votre temps.
              </p>
              <div className="flex items-center text-pink-400 text-sm">
                <Sparkles className="h-4 w-4 mr-2" />
                <span>Votre valeur, vos règles</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Questionnaires Section */}
      <section id="construisez-avec-nous" className="relative py-20 border-t border-white/5">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            {/* Section Header */}
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Construisez avec nous
            </h2>
            <p className="text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed">
              Si vous souhaitez construire avec nous la plateforme, nous vous invitons à partager 
              votre expérience au travers de nos deux questionnaires.
            </p>

            {/* Two Questionnaire Cards */}
            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {/* Learner Questionnaire */}
              <a 
                href={QUESTIONNAIRE_LEARNER}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-cyan-400/40 transition-all duration-300 cursor-pointer"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative flex flex-col items-center text-center">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl mb-4 shadow-lg shadow-cyan-500/20 group-hover:scale-110 transition-transform">
                    <GraduationCap className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Questionnaire Apprenant
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">
                    Vous souhaitez apprendre et être inspiré par des experts
                  </p>
                  <span className="inline-flex items-center text-cyan-400 text-sm font-medium group-hover:text-cyan-300 transition-colors">
                    Répondre au questionnaire
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </span>
                </div>
              </a>

              {/* Expert Questionnaire */}
              <a 
                href={QUESTIONNAIRE_EXPERT}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-pink-400/40 transition-all duration-300 cursor-pointer"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-pink-500/5 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative flex flex-col items-center text-center">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl mb-4 shadow-lg shadow-pink-500/20 group-hover:scale-110 transition-transform">
                    <Lightbulb className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Questionnaire Expert
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">
                    Vous souhaitez transmettre votre expertise
                  </p>
                  <span className="inline-flex items-center text-pink-400 text-sm font-medium group-hover:text-pink-300 transition-colors">
                    Répondre au questionnaire
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </span>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Final CTA - Rejoignez la communauté */}
      <section className="relative py-24 border-t border-white/5">
        <div className="absolute inset-0 bg-gradient-to-t from-cyan-500/5 via-transparent to-transparent pointer-events-none"></div>
        <div className="container mx-auto px-6 text-center relative">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-white">
            Rejoignez la communauté OpenSapiens
          </h2>
          <p className="text-lg text-gray-400 mb-10 max-w-xl mx-auto">
            Des professionnels partagent et apprennent sur OpenSapiens. Et vous ?
          </p>
          <Button
            size="lg"
            className="bg-gradient-to-r from-cyan-500 to-cyan-400 hover:from-cyan-400 hover:to-cyan-300 text-gray-900 font-semibold text-lg px-10 py-6 rounded-xl shadow-lg shadow-cyan-500/25 transition-all hover:shadow-cyan-500/40 hover:scale-105"
            onClick={() => navigate('/signup')}
          >
            Commencer gratuitement
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-10">
        <div className="container mx-auto px-6 text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <img
              src={LOGO_DARK}
              alt=""
              className="h-8 w-8 object-contain"
            />
            <span className="text-lg font-semibold bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              OpenSapiens
            </span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-gray-500 mb-3">
            <a href="/legal/cgu" data-testid="footer-cgu-link" className="hover:text-cyan-300 transition-colors">CGU</a>
            <span aria-hidden="true">·</span>
            <a href="/legal/privacy" data-testid="footer-privacy-link" className="hover:text-cyan-300 transition-colors">Confidentialité</a>
            <span aria-hidden="true">·</span>
            <a href="/legal/mentions" data-testid="footer-mentions-link" className="hover:text-cyan-300 transition-colors">Mentions légales</a>
            <span aria-hidden="true">·</span>
            <a href="mailto:info@opensapiens.ch" className="hover:text-cyan-300 transition-colors">info@opensapiens.ch</a>
          </div>
          <p className="text-gray-500 text-sm">
            © 2026 OpenSapiens. Plateforme suisse de partage de connaissances.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
